import * as React from 'react';

import { Recipient } from 'api/api';

import { Spinner } from '~src/commons/components';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { AriaGrid } from '~src/ui/aria-grid';
import { AnimatedButton } from '~src/ui/button';
import { DialogLayout } from '~src/ui/dialog-layout';
import { useListState } from '~src/ui/list-state';

import { AddButtonUI } from '../components/add-button';
import { useFSMContext } from '../context/use-fsm-context';
import { prepareNewAddress } from '../utils/prepare-address';
import { AddressListItem } from './address-list-item';

interface AddressListFSMProps {
  handleClose: () => void;
  handleSubmit: (address: Recipient) => void;
  handleAddNewAddress: (address: Recipient) => void;
  handleOpenAddress: (address: Recipient) => void;
}

export const BillingAddressListFSM: React.FC<AddressListFSMProps> = ({
  handleClose,
  handleSubmit,
  handleAddNewAddress,
  handleOpenAddress,
}) => {
  const globalState = useGlobalState();
  const { tICU } = useTranslations();
  const { billingState } = useFSMContext();

  const getAddresses = () => {
    if (globalState.address_book) {
      return globalState.address_book.addresses!;
    } else {
      return [globalState.billing_address!];
    }
  };
  const defaultSelectedItem = globalState.billing_address?.id
    ? globalState.billing_address.id
    : globalState.address_book?.addresses?.[0].id;

  const listState = useListState({
    items: getAddresses(),
    getKey: (address: Recipient) => address.id || '',
    defaultSelectedKey: defaultSelectedItem || '',
  });

  const saveAddress = () => {
    const currentItem = listState.getSelectedItem();
    if (currentItem) {
      handleSubmit(currentItem);
    }
  };

  const newAddress = prepareNewAddress(
    globalState.address_form_configuration?.billing_features,
    globalState.prefill_billing
  );

  const listElement = (
    <AriaGrid listState={listState}>
      {(address: Recipient) => {
        return (
          <AddressListItem
            key={address.id}
            address={address}
            listState={listState}
            handleClick={() => handleOpenAddress(address)}
          />
        );
      }}
    </AriaGrid>
  );

  return (
    <DialogLayout
      title={tICU('widget.billing_address.ADDRESS_LIST_MODAL.TITLE')}
      onClose={handleClose}
    >
      {listElement}
      <AddButtonUI
        label={tICU('widget.delivery_address.ADD_DIFFERENT_ADDRESS')}
        onClick={() => handleAddNewAddress(newAddress)}
      />
      <AnimatedButton onClick={saveAddress} animKey={`${billingState?.meta.fetching}`}>
        {billingState?.meta.fetching ? (
          <Spinner size="small" />
        ) : (
          tICU('widget.delivery_address.SAVE_CHOICE_BTN')
        )}
      </AnimatedButton>
    </DialogLayout>
  );
};
